import React, { useState } from 'react';
import {
  useGetProvenanceQueueQuery,
  useReviewProvenanceRecordMutation,
  useBatchReviewProvenanceRecordsMutation,
  useDeleteProvenanceRecordMutation,
  useBatchDeleteProvenanceRecordsMutation,
} from '../api/provenanceApi';
import type {
  ProvenanceRecord,
  GroupedProvenanceRecord,
  KnowledgeObjectInReview,
} from '../types/provenance.types';
import ProvenanceRecordDetail from './ProvenanceRecordDetail';

const ProvenanceQueuePage: React.FC = () => {
  const [status, setStatus] = useState<string>('queued');
  const [page, setPage] = useState<number>(1);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [selectedKnowledgeObjects, setSelectedKnowledgeObjects] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch } = useGetProvenanceQueueQuery({
    status,
    page,
    page_size: 20,
    grouped: true, // Enable grouped view
  });

  const [reviewRecord] = useReviewProvenanceRecordMutation();
  const [batchReview] = useBatchReviewProvenanceRecordsMutation();
  const [deleteRecord] = useDeleteProvenanceRecordMutation();
  const [batchDelete] = useBatchDeleteProvenanceRecordsMutation();

  const handleReview = async (recordId: string, action: 'approved' | 'rejected' | 'needs_revision') => {
    try {
      await reviewRecord({
        id: recordId,
        review: { action },
      }).unwrap();
      refetch();
      if (selectedRecord === recordId) {
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Error reviewing record:', error);
    }
  };

  const handleBatchReview = async (action: 'approved' | 'rejected') => {
    if (selectedKnowledgeObjects.size === 0) return;

    try {
      await batchReview({
        record_ids: Array.from(selectedKnowledgeObjects),
        action,
      }).unwrap();
      setSelectedKnowledgeObjects(new Set());
      refetch();
    } catch (error) {
      console.error('Error batch reviewing records:', error);
    }
  };

  const handleDelete = async (recordId: string) => {
    if (!window.confirm('Are you sure you want to delete this provenance record? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteRecord(recordId).unwrap();
      refetch();
      if (selectedRecord === recordId) {
        setSelectedRecord(null);
      }
      const newSelection = new Set(selectedKnowledgeObjects);
      newSelection.delete(recordId);
      setSelectedKnowledgeObjects(newSelection);
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record. Please try again.');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedKnowledgeObjects.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedKnowledgeObjects.size} knowledge object${selectedKnowledgeObjects.size !== 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }

    try {
      await batchDelete({
        record_ids: Array.from(selectedKnowledgeObjects),
      }).unwrap();
      setSelectedKnowledgeObjects(new Set());
      setSelectedRecord(null);
      refetch();
    } catch (error) {
      console.error('Error batch deleting records:', error);
      alert('Failed to delete records. Please try again.');
    }
  };

  const toggleKnowledgeObjectSelection = (koId: string) => {
    const newSelection = new Set(selectedKnowledgeObjects);
    if (newSelection.has(koId)) {
      newSelection.delete(koId);
    } else {
      newSelection.add(koId);
    }
    setSelectedKnowledgeObjects(newSelection);
  };

  const toggleGroupExpanded = (query: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(query)) {
      newExpanded.delete(query);
    } else {
      newExpanded.add(query);
    }
    setExpandedGroups(newExpanded);
  };

  const isGrouped = data?.grouped === true;
  
  // Type guard function to check if results are grouped
  const isGroupedRecord = (item: ProvenanceRecord | GroupedProvenanceRecord): item is GroupedProvenanceRecord => 'knowledge_objects' in item && 'discovery_query' in item && 'record_count' in item;
  
  // When grouped=true, all results are GroupedProvenanceRecord[]
  // When grouped=false or undefined, all results are ProvenanceRecord[]
  const groupedResults: GroupedProvenanceRecord[] = isGrouped && data?.results 
    ? (data.results as GroupedProvenanceRecord[])
    : [];
  
  const flatResults: ProvenanceRecord[] = !isGrouped && data?.results
    ? (data.results as ProvenanceRecord[])
    : [];

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-gray-200 rounded w-24"></div>
            ))}
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800 font-medium">Error loading provenance records</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Provenance Review Queue</h1>
        {/* Batch Actions */}
        {selectedKnowledgeObjects.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">
              {selectedKnowledgeObjects.size} selected:
            </span>
            <button
              onClick={() => handleBatchReview('approved')}
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
            >
              Approve
            </button>
            <button
              onClick={() => handleBatchReview('rejected')}
              className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
            >
              Reject
            </button>
            <button
              onClick={handleBatchDelete}
              className="inline-flex items-center rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
            >
              Delete
            </button>
            <button
              onClick={() => setSelectedKnowledgeObjects(new Set())}
              className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content Grid with Tabs, List and Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tabs spanning both columns - centered across full width */}
        <div className="lg:col-span-2 border-b border-gray-200 flex justify-center">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => {
              setStatus('queued');
              setSelectedRecord(null);
            }}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
              status === 'queued'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Queued
            {status === 'queued' && data?.count ? ` (${data.count})` : ''}
          </button>
          <button
            onClick={() => {
              setStatus('in_review');
              setSelectedRecord(null);
            }}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
              status === 'in_review'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            In Review
          </button>
          <button
            onClick={() => {
              setStatus('approved');
              setSelectedRecord(null);
            }}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
              status === 'approved'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => {
              setStatus('rejected');
              setSelectedRecord(null);
            }}
            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
              status === 'rejected'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Rejected
          </button>
        </nav>
        </div>

        {/* Grouped Table View */}
        <div className="space-y-3">
          {isGrouped && groupedResults.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-sm font-semibold text-gray-900">No records found</h3>
              <p className="mt-2 text-sm text-gray-500">There are no records with status "{status}"</p>
            </div>
          ) : isGrouped ? (
            groupedResults.map((group) => {
              const isExpanded = expandedGroups.has(group.discovery_query);
              const allSelected = group.knowledge_objects.every((ko) => selectedKnowledgeObjects.has(ko.id));
              const someSelected = group.knowledge_objects.some((ko) => selectedKnowledgeObjects.has(ko.id));

              return (
                <div
                  key={group.discovery_query}
                  className="bg-white rounded-lg border shadow-sm ring-1 ring-gray-900/5"
                >
                  {/* Group Header */}
                  <div
                    className="p-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleGroupExpanded(group.discovery_query)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSelection = new Set(selectedKnowledgeObjects);
                        if (allSelected) {
                          // Unselect all
                          group.knowledge_objects.forEach((ko) => newSelection.delete(ko.id));
                        } else {
                          // Select all
                          group.knowledge_objects.forEach((ko) => newSelection.add(ko.id));
                        }
                        setSelectedKnowledgeObjects(newSelection);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <svg
                          className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                          {group.discovery_query}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {group.knowledge_objects.length} knowledge object{group.knowledge_objects.length !== 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(group.earliest_created).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Knowledge Objects */}
                  {isExpanded && (
                    <div className="border-t border-gray-200">
                      {group.knowledge_objects.map((ko) => (
                        <div
                          key={ko.id}
                          className={`p-3 ml-10 flex items-start gap-3 border-t first:border-t-0 hover:bg-gray-50 cursor-pointer ${
                            selectedRecord === ko.id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setSelectedRecord(ko.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedKnowledgeObjects.has(ko.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleKnowledgeObjectSelection(ko.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 line-clamp-2">
                              {ko.content_preview}
                            </p>
                            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                              <span className="inline-flex items-center gap-1">
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                {ko.source_type}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                {ko.has_pending_review && (
                                  <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                                    Pending Review
                                  </span>
                                )}
                                {ko.review_count > 0 && (
                                  <span className="text-gray-500">
                                    {ko.review_count} review{ko.review_count !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            // Fallback to flat list if not grouped
            flatResults.map((record) => (
              <div
                key={record.id}
                className={`bg-white rounded-lg border shadow-sm ring-1 ring-gray-900/5 cursor-pointer transition-all duration-200 ${
                  selectedRecord === record.id
                    ? 'ring-2 ring-blue-500 bg-blue-50 shadow-md'
                    : 'hover:shadow-md hover:ring-gray-900/10'
                }`}
                onClick={() => setSelectedRecord(record.id)}
              >
                <div className="p-4 flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedKnowledgeObjects.has(record.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleKnowledgeObjectSelection(record.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">
                      {record.discovery_query?.substring(0, 150) || 'No query text'}
                      {record.discovery_query && record.discovery_query.length > 150 && '...'}
                    </p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {record.citation_count} citation{record.citation_count !== 1 ? 's' : ''}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(record.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Record Detail */}
        {selectedRecord ? (
          <div>
            <ProvenanceRecordDetail
              recordId={selectedRecord}
              onClose={() => setSelectedRecord(null)}
              onReview={handleReview}
              onDelete={handleDelete}
              currentStatus={status}
            />
          </div>
        ) : (
          <div className="hidden lg:flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 border-dashed min-h-[400px]">
            <div className="text-center p-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-sm font-semibold text-gray-900">Select a knowledge object</h3>
              <p className="mt-2 text-sm text-gray-500">Choose an item from the {status} list to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && (isGrouped ? groupedResults.length > 0 : flatResults.length > 0) && (
        <div className="flex items-center justify-center gap-4">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {page}</span>
          <button
            disabled={!data || (isGrouped ? groupedResults.length < 20 : flatResults.length < 20)}
            onClick={() => setPage(page + 1)}
            className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ProvenanceQueuePage;
